import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import { getPlanFormats, savePlanFormatBackups } from '@/api/planFormatsService';
import { getPlans, PlanListItem } from '@/api/plansService';
import { queryKeys } from '@/lib/queryKeys';
import { toaster } from '@/components/ui/toaster';

type Values = Record<string, string>;

const planOptions = (plans: PlanListItem[] | undefined) =>
  (plans ?? []).map((p) => (
    <option key={p.id} value={p.id}>
      {p.generable ? '' : '✕ '}
      {p.displayName}
    </option>
  ));

const BackupPlansModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const queryClient = useQueryClient();
  const [values, setValues] = useState<Values>({});

  const { data: formats } = useQuery({ queryKey: queryKeys.pv2PlanFormats, queryFn: getPlanFormats, enabled: open });
  const { data: generics } = useQuery({
    queryKey: queryKeys.pv2Plans({ planType: 'generic', page: 1 }),
    queryFn: () => getPlans({ planType: 'generic', page: 1, pageSize: 100 }),
    enabled: open,
  });
  useEffect(() => {
    if (formats) setValues(Object.fromEntries(formats.map((f) => [f.id, f.backupPlanId ?? ''])));
  }, [formats]);

  const set = (formatId: string, value: string) => setValues((v) => ({ ...v, [formatId]: value }));

  const save = useMutation({
    mutationFn: () =>
      savePlanFormatBackups(
        (formats ?? []).map((f) => ({ formatId: f.id, backupPlanId: values[f.id] || null }))
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.pv2PlanFormats });
      toaster.create({ title: "Plans d'attente enregistrés", type: 'success' });
      onClose();
    },
    onError: (error) => {
      const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Enregistrement impossible.';
      toaster.create({ title: 'Enregistrement impossible', description: message, type: 'error' });
    },
  });

  const nonGenerable = (formats ?? []).filter((f) => {
    const backup = generics?.items.find((p) => p.id === values[f.id]);
    return backup !== undefined && !backup.generable;
  });

  return (
    <Modal open={open} onClose={onClose} title="Plans d'attente" size="lg">
      <div className="space-y-4 px-6 py-5">
        <p className="text-xs text-gray-400">
          Plan d'attente : suivi en attendant le début de la préparation spécifique.
        </p>
        {(formats ?? []).map((f, i, all) => (
          <div key={f.id}>
            {all[i - 1]?.raceTypeId !== f.raceTypeId && (
              <p className="mb-2 mt-2 text-xs font-semibold uppercase text-gray-500">{f.raceTypeLabel}</p>
            )}
            <Select
              label={`${f.label} (${f.slug}) · plan d'attente`}
              value={values[f.id] ?? ''}
              onChange={(e) => set(f.id, e.target.value)}
            >
              <option value="">— Aucun —</option>
              {planOptions(generics?.items)}
            </Select>
          </div>
        ))}
        {nonGenerable.length > 0 && (
          <p className="text-xs text-warning-600">
            Attention : un plan choisi pour {nonGenerable.map((f) => f.slug).join(', ')} n'est pas générable.
          </p>
        )}
      </div>
      <div className="flex justify-end gap-2 border-t border-gray-200 px-6 py-4 dark:border-gray-800">
        <Button variant="ghost" onClick={onClose}>
          Annuler
        </Button>
        <Button onClick={() => save.mutate()} disabled={save.isPending}>
          Enregistrer
        </Button>
      </div>
    </Modal>
  );
};

export default BackupPlansModal;
